import { useState } from "react";
import ThemeToggle from "../components/ThemeToggle";
import { Navigate } from "react-router-dom";
import { Loader2, Lock, Mail, ShieldCheck, Sparkles } from "lucide-react";
import { useFarmaciaAuth } from "../context/FarmaciaAuthContext";
import LoginAreaLinks from "../components/LoginAreaLinks";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const LOGO_URL = "/images/logo-pharma.png";
const LOGO_MARK_URL = "/images/logo-pharma-mark.png";

export default function FarmaciaLogin() {
  const { login, isAuthenticated, loading } = useFarmaciaAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!loading && isAuthenticated) {
    return <Navigate to="/farmacia" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#f8fbfa] text-slate-900">
      <ThemeToggle className="fixed right-3 top-3 z-50" testId="theme-toggle-login" />
      <div className="grid min-h-screen lg:grid-cols-[0.9fr_1.1fr]">
        <aside className="relative hidden overflow-hidden bg-gradient-to-br from-[#f3faf8] via-white to-[#eef8f4] px-10 py-10 lg:flex lg:flex-col">
          <div className="relative z-10 max-w-md">
            <img src={LOGO_URL} alt="Pharma by Affidya" className="mb-9 h-12 w-auto" />
            <h1 className="text-[2rem] font-semibold leading-tight tracking-tight text-slate-900">
              Area dedicata alle farmacie partner
            </h1>
            <p className="mt-4 text-sm leading-6 text-slate-600">
              Consulta anagrafiche e terapie, gestisci ordini e comunicati con le strutture Affidya in un&apos;unica interfaccia chiara e sicura.
            </p>

            <div className="mt-9 space-y-5">
              {[
                {
                  icon: ShieldCheck,
                  title: "Accesso riservato",
                  text: "Solo le strutture collegate al tuo account sono visibili.",
                },
                {
                  icon: Sparkles,
                  title: "Operatività quotidiana",
                  text: "Terapie, ricette e comunicazioni sempre a portata di mano.",
                },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100/80 text-emerald-700">
                    <item.icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-900">{item.title}</span>
                    <span className="mt-0.5 block text-xs leading-5 text-slate-500">{item.text}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="absolute -bottom-24 -left-28 h-72 w-[32rem] rounded-[55%] bg-emerald-200/40 blur-2xl" />
          <div className="absolute -bottom-16 left-0 h-48 w-[115%] rounded-[55%_45%_0_0] bg-emerald-100/70" />
          <div className="absolute bottom-[-2rem] right-[-18%] h-52 w-[78%] rounded-tl-[100%] bg-[#d7f1e8]/70" />
        </aside>

        <main className="flex min-h-screen items-center justify-center px-4 py-6 sm:px-6 lg:px-8">
          <div className="w-full max-w-[440px]">
            <div className="mb-5 text-center lg:hidden">
              <img src={LOGO_URL} alt="Pharma by Affidya" className="mx-auto mb-3 h-12 w-auto" />
            </div>

            <section className="rounded-[1.6rem] border border-white/80 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-7">
              <div className="mb-6 text-center">
                <img
                  src={LOGO_MARK_URL}
                  alt="Pharma by Affidya"
                  className="mx-auto mb-3 h-12 w-12 rounded-2xl object-contain"
                />
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Accedi al portale</h2>
                <p className="mt-1 text-sm text-slate-500">Usa le credenziali fornite da Affidya</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="farmacia-email" className="text-sm font-medium text-slate-700">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="farmacia-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="farmacia@esempio.it"
                      className="h-11 rounded-xl border-slate-200 bg-slate-50/50 pl-10 text-sm focus-visible:ring-emerald-500/30"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="farmacia-password" className="text-sm font-medium text-slate-700">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="farmacia-password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="h-11 rounded-xl border-slate-200 bg-slate-50/50 pl-10 text-sm focus-visible:ring-emerald-500/30"
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="h-11 w-full rounded-xl bg-[#4CAF50] text-sm font-semibold text-white hover:bg-[#43A047]"
                >
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Accedi
                </Button>
              </form>

              <LoginAreaLinks current="farmacia" theme="emerald" className="mt-6" />
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
